import React from "react";
import { motion } from "framer-motion";
import { Sparkles, MapPin, Shield, Heart } from "lucide-react";
import { Palm } from "./Decor";
import { BrandSymbol } from "./BrandLogo";
import { useLang } from "../data/i18n";

const ICONS = {
    premium: Sparkles,
    local: MapPin,
    stressfree: Shield,
    surmesure: Heart,
};

export default function WhyUs() {
    const { t } = useLang();
    return (
        <section
            className="relative bg-ocean py-24 md:py-32 overflow-hidden"
            data-testid="why-us-section"
        >
            <Palm className="absolute -top-10 -right-20 w-96 h-96 opacity-10" color="#77CABA" />
            <Palm className="absolute -bottom-16 -left-20 w-80 h-80 opacity-10 scale-x-[-1]" color="#77CABA" />
            <BrandSymbol
                size={640}
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-[0.06]"
            />

            <div className="relative max-w-[1400px] mx-auto px-5 md:px-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                    <div className="lg:col-span-5">
                        <p className="text-[11px] uppercase tracking-[0.3em] text-lagoon font-bold mb-5 flex items-center gap-3">
                            <span className="inline-block w-8 h-px bg-lagoon" />
                            {t.whyus.kicker}
                        </p>
                        <h2 className="font-serif font-normal text-cream text-5xl md:text-6xl leading-[0.95] tracking-tight">
                            {t.whyus.titleA}
                            <br />
                            <em className="coral-italic">{t.whyus.titleB}</em>.
                        </h2>
                        <p className="mt-8 text-cream/85 leading-relaxed max-w-md">
                            {t.whyus.intro}
                        </p>
                        <p className="mt-6 font-serif italic text-lagoon text-2xl leading-snug">
                            {t.whyus.quote}
                        </p>
                    </div>

                    <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-cream/10">
                        {t.whyus.reasons.map((r, i) => {
                            const Icon = ICONS[r.id];
                            return (
                                <motion.div
                                    key={r.id}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{
                                        duration: 0.6,
                                        delay: i * 0.1,
                                    }}
                                    className="bg-ocean p-8 md:p-10 hover:bg-ocean-600 transition-colors group"
                                    data-testid={`why-card-${r.id}`}
                                >
                                    <div className="w-14 h-14 flex items-center justify-center bg-lagoon text-ocean mb-6 group-hover:scale-110 transition-transform">
                                        <Icon size={24} strokeWidth={1.7} />
                                    </div>
                                    <h3 className="font-serif text-cream text-3xl mb-3">
                                        {r.title}
                                    </h3>
                                    <p className="text-cream/80 text-sm leading-relaxed">
                                        {r.text}
                                    </p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
}
